import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['form', 'modal', 'backdrop', 'container'];

  connect() {
    // Add pulse animation styles to document if not already present
    this.addPulseAnimation();

    const delay = this.isTestEnv() ? 0 : 2000;
    if (this.hasFormTarget) {
      this.startPreparationAnimation();
      this.startTimeout = setTimeout(() => {
        this.startFunnel();
      }, delay);
    }

    // Watch for completion content
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const completionDiv = this.element.querySelector(
            '[data-completion-message]'
          );
          if (completionDiv) {
            this.autoclose();
            this.observer.disconnect();
          }
        }
      });
    });

    // Start observing
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnect() {
    clearTimeout(this.startTimeout);
    clearTimeout(this.autocloseTimeout);
    clearTimeout(this.preparationTimeout);
    document.body.classList.remove('overflow-hidden');
    this.removePreparationElements();
    // Clean up any remaining preparation overlay
    const overlay = document.getElementById(
      'endless-funnel-preparation-overlay'
    );
    if (overlay) {
      overlay.remove();
    }
    this.removePulseAnimation();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // Detect test environment (flag injected via layout)
  isTestEnv() {
    return document.documentElement.dataset.testEnv === 'true'
  }

  startPreparationAnimation() {
    // Create preparation indicator
    this.createPreparationIndicator();

    const delay = this.isTestEnv() ? 0 : 1000;
    // Start subtle page dimming after 1 second
    this.preparationTimeout = setTimeout(() => {
      this.dimPageContent();
    }, delay);
  }

  createPreparationIndicator() {
    // Create a floating notification that appears at the bottom
    const indicator = document.createElement('div');
    indicator.id = 'endless-funnel-preparation';
    indicator.innerHTML = `
      <div class="fixed bottom-4 left-1/2 lg:left-4 z-40"
           style="transform: translateX(-50%) translateY(100%); transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);">
        <div class="bg-white border-2 border-blue-200 rounded-lg shadow-lg p-4 w-80 max-w-sm lg:w-auto lg:max-w-sm">
          <div class="flex items-center space-x-3">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg class="w-5 h-5 text-ph24_blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                     style="animation: endless-funnel-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">Einen Moment bitte...</p>
              <p class="text-xs text-gray-600">Zur bedarfsgerechten Beratung haben wir noch ein paar Fragen</p>
            </div>
          </div>
          <div class="mt-2">
            <div class="w-full bg-gray-200 rounded-full h-1">
              <div class="bg-ph24_blue h-1 rounded-full preparation-progress"
                   style="width: 0%; transition: width 2000ms cubic-bezier(0.4, 0, 0.2, 1);"></div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add keyframes for pulse animation
    this.addPulseAnimation();

    document.body.appendChild(indicator);

    // Animate in the notification
    requestAnimationFrame(() => {
      const notification = indicator.querySelector('.fixed');
      // For mobile: center horizontally, slide up. For desktop: align left, slide up
      notification.style.transform =
        window.innerWidth >= 1024
          ? 'translateX(0) translateY(0)'
          : 'translateX(-50%) translateY(0)';

      // Start progress bar animation
      const progressBar = indicator.querySelector('.preparation-progress');
      requestAnimationFrame(() => {
        progressBar.style.width = '100%';
      });
    });
  }

  addPulseAnimation() {
    // Only add if not already present
    if (!document.getElementById('endless-funnel-pulse-animation')) {
      const style = document.createElement('style');
      style.id = 'endless-funnel-pulse-animation';
      style.textContent = `
        @keyframes endless-funnel-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `;
      document.head.appendChild(style);
    }
  }

  removePulseAnimation() {
    const style = document.getElementById('endless-funnel-pulse-animation');
    if (style) {
      style.remove();
    }
  }

  dimPageContent() {
    // Add a subtle overlay to prepare for modal
    const overlay = document.createElement('div');
    overlay.id = 'endless-funnel-preparation-overlay';
    overlay.className = 'fixed inset-0 bg-black z-30 pointer-events-none';
    overlay.style.cssText = `
      opacity: 0;
      transition: opacity 1000ms ease-out;
    `;
    document.body.appendChild(overlay);

    // Gradually increase opacity
    requestAnimationFrame(() => {
      overlay.style.opacity = '0.1';
    });
  }

  removePreparationElements() {
    // Remove preparation indicator
    const indicator = document.getElementById('endless-funnel-preparation');
    if (indicator) {
      const notification = indicator.querySelector('.fixed');
      if (notification) {
        // Slide down while maintaining horizontal position
        const isDesktop = window.innerWidth >= 1024;
        notification.style.transform = isDesktop
          ? 'translateX(0) translateY(100%)'
          : 'translateX(-50%) translateY(100%)';
        setTimeout(() => indicator.remove(), 300);
      } else {
        indicator.remove();
      }
    }
  }

  startFunnel() {
    // Clean up preparation elements before starting funnel
    this.removePreparationElements();

    // Use requestSubmit with fallback for older browsers
    if (this.formTarget.requestSubmit) {
      this.formTarget.requestSubmit();
    } else {
      // Fallback for browsers that don't support requestSubmit
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true,
      });
      this.formTarget.dispatchEvent(submitEvent);
      if (!submitEvent.defaultPrevented) {
        this.formTarget.submit();
      }
    }
  }

  modalTargetConnected() {
    document.body.classList.add('overflow-hidden');

    // Smoothly transition from preparation overlay to modal blur
    const preparationOverlay = document.getElementById(
      'endless-funnel-preparation-overlay'
    );
    const contentEl = document.getElementById('completion-page-content');

    if (preparationOverlay && contentEl) {
      // Gradually increase overlay opacity while adding blur
      preparationOverlay.style.opacity = '0.2';
      contentEl.style.cssText = `
        filter: blur(3px);
        transition: filter 0.5s ease-in-out;
      `;

      // Remove preparation overlay after blur transition
      const delay = this.isTestEnv() ? 0 : 500;
      setTimeout(() => {
        if (preparationOverlay) {
          preparationOverlay.remove();
        }
      }, delay);
    } else if (contentEl && !contentEl.style.filter) {
      // Fallback if no preparation overlay exists
      contentEl.style.cssText = `
        filter: blur(3px);
        transition: filter 0.3s ease-in-out;
      `;
    }

    // Animate in the backdrop and the container
    requestAnimationFrame(() => {
      if (this.hasBackdropTarget) {
        this.backdropTarget.style.opacity = '1';
      }
      if (this.hasContainerTarget) {
        this.containerTarget.style.opacity = '1';
        this.containerTarget.style.transform = 'scale(1)';
      }
    });
  }

  modalTargetDisconnected() {
    document.body.classList.remove('overflow-hidden');
  }

  closeModal() {
    // Remove blur
    const contentEl = document.getElementById('completion-page-content');
    if (contentEl) {
      contentEl.style.filter = '';
      contentEl.style.transition = '';
    }

    // Animate out
    if (this.hasBackdropTarget) {
      this.backdropTarget.style.opacity = '0';
    }
    if (this.hasContainerTarget) {
      this.containerTarget.style.opacity = '0';
      this.containerTarget.style.transform = 'scale(0.95)';
    }

    // Wait for animation then remove
    setTimeout(() => {
      this.modalTarget?.remove();
    }, 300);
  }

  autoclose() {
    if (!this.hasModalTarget) {
      return;
    }

    const delay = this.isTestEnv() ? 0 : 1500;
    this.autocloseTimeout = setTimeout(() => {
      this.closeModal();
    }, delay);
  }

  showSpinnerOnSubmit(event) {
    const submitter = event.submitter;

    if (submitter) {
      const optionText = submitter.querySelector('.option-text');
      const spinner = submitter.querySelector('.spinner');

      if (optionText && spinner) {
        // --- THIS IS THE ONLY CHANGE ---
        // Use 'invisible' to keep the button's size stable.
        optionText.classList.add('invisible');

        spinner.classList.remove('hidden');
      }
    }
  }
}